// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Bottom navigation bar
// 5 tabs: Caisse, Tables, Reservations, Stats, Tickets
// ---------------------------------------------------------------------------

import { useAppStore } from '@/store/app-store';
import type { Screen } from '@/lib/types';

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

function CaisseIcon({ active }: { active: boolean }) {
  const cls = active ? 'text-accent-light' : 'text-gray-400';
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <path d="M12 6V2" />
      <path d="M6 12h2" />
      <path d="M10 12h2" />
      <path d="M14 12h2" />
      <circle cx="12" cy="15" r="1" />
    </svg>
  );
}

function TablesIcon({ active }: { active: boolean }) {
  const cls = active ? 'text-accent-light' : 'text-gray-400';
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v10" />
      <path d="M19 8v10" />
      <path d="M3 18h18" />
    </svg>
  );
}

function ReservationsIcon({ active }: { active: boolean }) {
  const cls = active ? 'text-accent-light' : 'text-gray-400';
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4" />
      <path d="M8 2v4" />
      <path d="M3 10h18" />
      <path d="M9 16h6" />
    </svg>
  );
}

function StatsIcon({ active }: { active: boolean }) {
  const cls = active ? 'text-accent-light' : 'text-gray-400';
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

function TicketsIcon({ active }: { active: boolean }) {
  const cls = active ? 'text-accent-light' : 'text-gray-400';
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
      <path d="M4 2v20l4-2 4 2 4-2 4 2V2l-4 2-4-2-4 2Z" />
      <path d="M8 10h8" />
      <path d="M8 14h4" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Tab config
// ---------------------------------------------------------------------------

interface NavTab {
  screen: Screen;
  label: string;
  Icon: React.ComponentType<{ active: boolean }>;
}

const TABS: NavTab[] = [
  { screen: 'caisse', label: 'Caisse', Icon: CaisseIcon },
  { screen: 'tables', label: 'Tables', Icon: TablesIcon },
  { screen: 'reservations', label: 'Resas', Icon: ReservationsIcon },
  { screen: 'stats', label: 'Stats', Icon: StatsIcon },
  { screen: 'tickets', label: 'Tickets', Icon: TicketsIcon },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BottomNav() {
  const screen = useAppStore((s) => s.screen);
  const setScreen = useAppStore((s) => s.setScreen);

  return (
    <nav className="fixed bottom-0 left-0 right-0 h-16 z-40 bg-surface-light border-t border-white/10 flex items-center justify-around px-1">
      {TABS.map(({ screen: tabScreen, label, Icon }) => {
        const isActive = screen === tabScreen;
        return (
          <button
            key={tabScreen}
            type="button"
            onClick={() => setScreen(tabScreen)}
            className={`flex flex-col items-center justify-center gap-0.5 flex-1 min-h-[44px] py-1.5 rounded-lg transition-colors duration-150 ${
              isActive ? 'bg-accent/10' : 'active:bg-white/5'
            }`}
          >
            <Icon active={isActive} />
            <span
              className={`text-[10px] font-medium ${
                isActive ? 'text-accent-light' : 'text-gray-400'
              }`}
            >
              {label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
